import { useEffect, useState } from 'react';
import { AxiosService } from 'src/axios/axios.service';
import type { tLeadAgentPendingPayment } from './LeadAgentTransferPendingBanner.types';

// RET-1764 — "¿este workspace tiene una factura del Lead Agent esperando transferencia?"
//
// Cuando el cliente paga por depósito/transferencia, el cobro del Lead Agent no se
// carga a una tarjeta: se le emite una factura y Stripe se la manda por correo. Un
// correo más en la bandeja es fácil de no ver, y el cliente terminaba enterándose de
// que debía cuando el bot se apagaba. Este hook alimenta el aviso dentro de la app.
//
// Se lee directo de billing (workspace-scoped), igual que la señal de "pausado" de la
// barra de chat, y NO por el proxy de bff-web: el dato nace en billing y no necesita
// composición.
//
// Refetch al volver a la pestaña (sin polling): así el aviso desaparece solo cuando el
// cliente ya transfirió y el webhook liquidó el cargo.
//
// Fail-closed: cargando / error / respuesta rara → no hay aviso. Nunca inventamos una
// deuda en pantalla.

type tPendingPaymentEnvelope = {
  data?: {
    pending?: boolean;
    amount_cents?: number;
    currency?: string;
    due_date?: string | null;
    invoice_url?: string | null;
  };
};

const NOT_PENDING: tLeadAgentPendingPayment = { pending: false };

/**
 * Valida la respuesta en runtime. Un aviso de cobro sin monto o sin fecha no se
 * muestra a medias: o está completo o no aparece.
 */
function parsePendingPayment(
  data: tPendingPaymentEnvelope['data'],
): tLeadAgentPendingPayment {
  if (!data || data.pending !== true) return NOT_PENDING;

  const amountCents = data.amount_cents;
  if (typeof amountCents !== 'number' || !Number.isFinite(amountCents)) {
    return NOT_PENDING;
  }

  const currency =
    typeof data.currency === 'string' && data.currency !== ''
      ? data.currency
      : null;
  if (!currency) return NOT_PENDING;

  return {
    pending: true,
    amountCents,
    currency,
    dueDate:
      typeof data.due_date === 'string' && data.due_date !== ''
        ? data.due_date
        : null,
    invoiceUrl:
      typeof data.invoice_url === 'string' && data.invoice_url !== ''
        ? data.invoice_url
        : null,
  };
}

// ── Fuente compartida ────────────────────────────────────────────────────────────────
//
// El estado vive FUERA de los componentes a propósito. Dos contenedores consultan esta
// señal —el banner de la factura, para pintarse; el de consumo, para cederle el paso— y si
// cada uno hiciera su propia llamada podrían divergir. Ahí el resultado no es sólo "se
// pintan los dos": si la lectura del banner de factura falla (se esconde) mientras la del
// banner de consumo tiene éxito (también se esconde, por prioridad), el cliente con deuda
// y al tope de tokens NO VE NINGÚN aviso de dinero. Un solo estado hace ese caso imposible
// por construcción, y de paso baja de dos llamadas a una por pantalla.
type tListener = () => void;

/**
 * RET-1875 / F1 — estado de carga REAL de la fuente compartida, con la
 * semántica de fuente del alert center (R3b):
 * - 'loading' SOLO durante el primer fetch de un workspace (cold load). Es la
 *   ventana donde el hold R3b evita el flicker capped→transfer: mientras no
 *   se sabe si hay factura, ni el aviso de tope ni el de factura se pintan.
 * - Los refetch por focus NO degradan el estado: el último valor resuelto
 *   sigue vigente mientras vuela la revalidación (stale-while-revalidate).
 * - 'error' es resuelto-desconocido: fail-closed (sin aviso), pero no retiene
 *   la evaluación de los avisos que dependen de esta fuente.
 */
export type tLeadAgentPendingPaymentStatus = 'loading' | 'ready' | 'error';

export type tLeadAgentPendingPaymentState = {
  status: tLeadAgentPendingPaymentStatus;
  payment: tLeadAgentPendingPayment;
};

const store: {
  workspaceId: string | null;
  value: tLeadAgentPendingPayment;
  status: tLeadAgentPendingPaymentStatus;
  listeners: Set<tListener>;
  controller: AbortController | null;
} = {
  workspaceId: null,
  value: NOT_PENDING,
  status: 'loading',
  listeners: new Set(),
  controller: null,
};

function publish(
  value: tLeadAgentPendingPayment,
  status: tLeadAgentPendingPaymentStatus,
) {
  store.value = value;
  store.status = status;
  store.listeners.forEach((listener) => listener());
}

function fetchShared(workspaceId: string) {
  store.controller?.abort();
  store.controller = new AbortController();
  AxiosService.get<tPendingPaymentEnvelope>(
    `workspaces/${workspaceId}/lead-agent/pending-payment`,
    {
      isBillingService: true,
      signal: store.controller.signal,
      headers: { 'x-workspace-id': workspaceId },
    },
  )
    .then((res) => {
      // Una respuesta de otro workspace (el usuario cambió mientras volaba) se descarta:
      // pintarla sería atribuirle una deuda ajena.
      if (store.workspaceId !== workspaceId) return;
      publish(parsePendingPayment(res?.data), 'ready');
    })
    .catch((e: unknown) => {
      const name = (e as { name?: string })?.name;
      // Los aborts (fetch superado / limpieza) se ignoran; cualquier error real esconde el
      // aviso.
      if (name === 'CanceledError' || name === 'AbortError') return;
      if (store.workspaceId !== workspaceId) return;
      publish(NOT_PENDING, 'error');
    });
}

const DISABLED_STATE: tLeadAgentPendingPaymentState = {
  // Sin workspace no hay nada que resolver: 'ready' + sin deuda, igual que las
  // fuentes deshabilitadas del alert center — un consumidor apagado jamás debe
  // retener la evaluación de nadie.
  status: 'ready',
  payment: NOT_PENDING,
};

function readStoreState(): tLeadAgentPendingPaymentState {
  return { status: store.status, payment: store.value };
}

/**
 * Suscripción compartida. Devuelve el estado compuesto {status, payment};
 * `useLeadAgentPendingPayment` (los banners) proyecta solo el payment para no
 * cambiar su contrato.
 */
export function useLeadAgentPendingPaymentState(
  workspaceId: string | undefined,
): tLeadAgentPendingPaymentState {
  const [state, setState] = useState<tLeadAgentPendingPaymentState>(
    workspaceId && store.workspaceId === workspaceId
      ? readStoreState()
      : DISABLED_STATE,
  );

  useEffect(() => {
    if (!workspaceId) {
      setState(DISABLED_STATE);
      return;
    }

    // Cambió el workspace: se descarta lo anterior antes de suscribir, para que nadie lea
    // la deuda del workspace previo. El status vuelve a 'loading': es el primer
    // fetch de ESTE workspace y el hold R3b aplica de nuevo.
    if (store.workspaceId !== workspaceId) {
      store.workspaceId = workspaceId;
      store.value = NOT_PENDING;
      store.status = 'loading';
    }

    // Conserva la identidad del estado cuando el publish no cambió nada: un
    // refetch por focus que devuelve lo mismo no debe re-renderizar a los
    // suscriptores (misma propiedad que ya cuidaba el publish original).
    const listener = () =>
      setState((previous) =>
        previous.status === store.status && previous.payment === store.value
          ? previous
          : readStoreState(),
      );
    store.listeners.add(listener);
    listener();

    // Sólo el primer suscriptor dispara la llamada; el segundo se cuelga del mismo estado.
    if (store.listeners.size === 1) fetchShared(workspaceId);

    const onFocus = () => fetchShared(workspaceId);
    window.addEventListener('focus', onFocus);

    return () => {
      store.listeners.delete(listener);
      window.removeEventListener('focus', onFocus);
      if (store.listeners.size === 0) {
        store.controller?.abort();
        store.controller = null;
      }
    };
  }, [workspaceId]);

  return state;
}

export function useLeadAgentPendingPayment(
  workspaceId: string | undefined,
): tLeadAgentPendingPayment {
  return useLeadAgentPendingPaymentState(workspaceId).payment;
}
